'use client';

import { useEffect, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';

import { useDebounce } from '@/hooks';

import type { BrowseFilter } from '@/constants';

interface UseSearchPageReturn {
  state: {
    searchQuery: string;
    status: string;
    activeTab: 'market' | 'profile';
  };
  handlers: {
    setSearchQuery: (value: string) => void;
    handleSearchChange: (value: string) => void;
    handleBrowseClick: (filter: BrowseFilter) => void;
    handleStatusChange: (status: string) => void;
    handleClearFilters: () => void;
    setActiveTab: (tab: 'market' | 'profile') => void;
  };
}

export const useSearchPage = (initialSearch = ''): UseSearchPageReturn => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const router = useRouter();

  const initialSearchFromUrl = searchParams.get('q') || '';
  const initialStatusFromUrl = searchParams.get('status') || 'all';

  const [searchQuery, setSearchQuery] = useState(initialSearch || initialSearchFromUrl);

  const [status, setStatus] = useState(initialStatusFromUrl);
  const [selectedFilter, setSelectedFilter] = useState<BrowseFilter | null>(null);
  const [activeTab, setActiveTab] = useState<'market' | 'profile'>('market');

  const [prevUrlQuery, setPrevUrlQuery] = useState(initialSearchFromUrl);
  const [prevUrlStatus, setPrevUrlStatus] = useState(initialStatusFromUrl);

  const currentUrlQuery = searchParams.get('q') || '';
  const currentUrlStatus = searchParams.get('status') || 'all';

  if (currentUrlQuery !== prevUrlQuery || currentUrlStatus !== prevUrlStatus) {
    setPrevUrlQuery(currentUrlQuery);
    setPrevUrlStatus(currentUrlStatus);
    setSearchQuery(currentUrlQuery);
    setStatus(currentUrlStatus);
  }

  const debouncedQuery = useDebounce(searchQuery, 400);
  const previousQueryRef = useRef(debouncedQuery);
  const previousStatusRef = useRef(status);

  useEffect(() => {
    if (previousQueryRef.current === debouncedQuery && previousStatusRef.current === status) return;

    previousQueryRef.current = debouncedQuery;
    previousStatusRef.current = status;

    const params = new URLSearchParams(window.location.search);

    if (debouncedQuery) {
      params.set('q', debouncedQuery);
    } else {
      params.delete('q');
    }

    if (status !== 'all') {
      params.set('status', status);
    } else {
      params.delete('status');
    }

    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
  }, [debouncedQuery, status, pathname, router]);

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value && selectedFilter) {
      setSelectedFilter(null);
    }
  };

  const handleBrowseClick = (filter: BrowseFilter) => {
    if (filter.path) {
      router.push(filter.path);
      return;
    }
    setSelectedFilter(filter);
    setSearchQuery('');
  };

  const handleStatusChange = (newStatus: string) => {
    setStatus(newStatus);
  };

  const handleClearFilters = () => {
    setStatus('all');
    setSelectedFilter(null);
  };

  return {
    state: {
      searchQuery,
      status,
      activeTab,
    },
    handlers: {
      setSearchQuery,
      handleSearchChange,
      handleBrowseClick,
      handleStatusChange,
      handleClearFilters,
      setActiveTab,
    },
  };
};
